import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { mapBackendCostsToUi } from '../../../../utils/constants/cost-mapper';
import { formatMoney } from '../../../../utils/constants/costs';
import { useTheme } from '../../../../utils/theme-context';

// One shared palette for BOTH charts (keys must match series.name / pie data.name exactly)
const COLOR_BY_NAME: Record<string, string> = {
    // top-level totals (only appear in stacked total row)
    Chats: '#60A5FA',
    Runs: '#A3E635',
    Other: '#CBD5E1',

    // chats breakdown
    'Get started': '#6B7280',
    'Scenario chat': '#F59E0B',
    Chat: '#3B82F6',

    // runs breakdown
    'Scenario runs': '#84CC16',
    'Property runs': '#EC4899',
    'Other runs': '#D1D5DB',
};

const CostsCharts = ({
    loading,
    backendResponse,
}: {
    loading?: boolean;
    backendResponse?: any;
}) => {
    const { theme } = useTheme();
    const ui = useMemo(() => mapBackendCostsToUi(backendResponse), [backendResponse]);

    const byKey = useMemo(() => {
        const m = new Map<string, { v: number; c: number; label: string }>();
        for (const it of ui.items || []) {
            m.set(it.key, {
                v: Number(it.totalUsd || 0),
                c: Number(it.calls || 0),
                label: it.label,
            });
        }
        return m;
    }, [ui.items]);

    const get = (key: string) => byKey.get(key) || { v: 0, c: 0, label: key };

    const getStarted = useMemo(() => get('get_started'), [byKey]);
    const scenarioChat = useMemo(() => get('scenario_chat'), [byKey]);
    const chat = useMemo(() => get('chat'), [byKey]);

    const runsTotalKey = useMemo(() => get('runs_total'), [byKey]);
    const scenarioRuns = useMemo(() => get('scenario_runs'), [byKey]);
    const propertyRuns = useMemo(() => get('property_runs'), [byKey]);

    const chatsTotal = useMemo(
        () => getStarted.v + scenarioChat.v + chat.v,
        [getStarted.v, scenarioChat.v, chat.v],
    );

    const runsBreakdownSum = useMemo(
        () => scenarioRuns.v + propertyRuns.v,
        [scenarioRuns.v, propertyRuns.v],
    );

    const totalUsd = Number(ui.totalUsd || 0);
    const totalCalls = Number(ui.calls || 0);

    const totalOther = useMemo(() => {
        const other = totalUsd - (chatsTotal + runsTotalKey.v);
        return Math.abs(other) > 1e-12 ? other : 0;
    }, [totalUsd, chatsTotal, runsTotalKey.v]);

    const runsOther = useMemo(() => {
        const other = runsTotalKey.v - runsBreakdownSum;
        return Math.abs(other) > 1e-12 ? other : 0;
    }, [runsTotalKey.v, runsBreakdownSum]);

    const stackedOption = useMemo(() => {
        const categories = ['Total', 'Chats', 'Runs'];

        const totalsByRow = [totalUsd, chatsTotal, runsTotalKey.v];

        const series = [
            { name: 'Chats', data: [Math.max(0, chatsTotal), 0, 0] },
            { name: 'Runs', data: [Math.max(0, runsTotalKey.v), 0, 0] },
            ...(totalOther !== 0 ? [{ name: 'Other', data: [Math.max(0, totalOther), 0, 0] }] : []),

            { name: 'Get started', data: [0, Math.max(0, getStarted.v), 0] },
            { name: 'Scenario chat', data: [0, Math.max(0, scenarioChat.v), 0] },
            { name: 'Chat', data: [0, Math.max(0, chat.v), 0] },

            { name: 'Scenario runs', data: [0, 0, Math.max(0, scenarioRuns.v)] },
            { name: 'Property runs', data: [0, 0, Math.max(0, propertyRuns.v)] },
            ...(runsOther !== 0 ? [{ name: 'Other runs', data: [0, 0, Math.max(0, runsOther)] }] : []),
        ];

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const items = Array.isArray(params) ? params : [params];
                    const idx = items?.[0]?.dataIndex ?? 0;
                    const rowName = categories[idx];
                    const rowTotal = Number(totalsByRow[idx] || 0);

                    const lines = items
                        .filter((p: any) => Number(p.value || 0) > 0)
                        .sort((a: any, b: any) => Number(b.value || 0) - Number(a.value || 0))
                        .map((p: any) => {
                            const v = Number(p.value || 0);
                            const pct = rowTotal > 0 ? (v / rowTotal) * 100 : 0;
                            return `${p.marker} ${p.seriesName}: <b>$${formatMoney(
                                v,
                            )}</b> <span style="color:#6b7280">(${pct.toFixed(1)}%)</span>`;
                        });

                    return [
                        `<div style="font-weight:700;margin-bottom:6px">${rowName}</div>`,
                        `<div style="color:#6b7280;margin-bottom:8px">Total: <b>$${formatMoney(
                            rowTotal,
                        )}</b></div>`,
                        ...lines,
                    ].join('<br/>');
                },
            },
            legend: {
                top: 0,
                icon: 'circle',
                itemWidth: 8,
                itemHeight: 8,
                textStyle: { color: '#6b7280' },
            },
            grid: { left: 12, right: 16, top: 40, bottom: 14, containLabel: true },
            xAxis: {
                type: 'value',
                axisLabel: {
                    color: '#6b7280',
                    formatter: (v: number) => `$${formatMoney(Number(v))}`,
                },
                splitLine: { lineStyle: { color: 'rgba(156,163,175,0.18)' } },
                axisLine: { lineStyle: { color: '#e5e7eb' } },
            },
            yAxis: {
                type: 'category',
                data: categories,
                axisTick: { show: false },
                axisLine: { show: false },
                axisLabel: { color: theme === 'dark' ? '#fff' : '#111827', fontWeight: 700 },
            },
            series: series.map((s) => ({
                name: s.name,
                type: 'bar',
                stack: 'sum',
                barWidth: 26,
                data: s.data,
                emphasis: { focus: 'series' },
                // 🔑 force consistent colors
                itemStyle: { borderRadius: 10, color: COLOR_BY_NAME[s.name] },
                label: {
                    show: true,
                    position: 'inside',
                    color: '#111827',
                    fontSize: 12,
                    backgroundColor: 'rgba(255,255,255,0.86)',
                    borderRadius: 6,
                    padding: [4, 6, 4, 6],
                    formatter: (p: any) => {
                        const v = Number(p.value || 0);
                        if (v <= 0) return '';
                        const rowTotal = Number(totalsByRow[p.dataIndex] || 0);
                        const pct = rowTotal > 0 ? v / rowTotal : 0;
                        if (pct < 0.07) return '';
                        return `$${formatMoney(v)}`;
                    },
                },
            })),
        };
    }, [
        theme,
        totalUsd,
        chatsTotal,
        runsTotalKey.v,
        totalOther,
        runsOther,
        getStarted.v,
        scenarioChat.v,
        chat.v,
        scenarioRuns.v,
        propertyRuns.v,
    ]);

    const pieOption = useMemo(() => {
        const rows = [
            { name: 'Chat', value: chat.v, calls: chat.c },
            { name: 'Property runs', value: propertyRuns.v, calls: propertyRuns.c },
            { name: 'Scenario runs', value: scenarioRuns.v, calls: scenarioRuns.c },
            { name: 'Get started', value: getStarted.v, calls: getStarted.c },
            { name: 'Scenario chat', value: scenarioChat.v, calls: scenarioChat.c },
        ];

        const total = rows.reduce((a, r) => a + r.value, 0);

        // 🔑 apply the same palette to slices
        const data = rows
            .filter((r) => r.value > 0)
            .map((r) => ({
                ...r,
                itemStyle: { color: COLOR_BY_NAME[r.name] },
            }));

        return {
            tooltip: {
                trigger: 'item',
                formatter: (p: any) => {
                    const v = Number(p.value || 0);
                    const pct = total > 0 ? (v / total) * 100 : 0;
                    const c = Number(p.data?.calls || 0);
                    return [
                        `<div style="font-weight:700;margin-bottom:6px">${p.name}</div>`,
                        `<div>Cost: <b>$${formatMoney(v)}</b></div>`,
                        `<div>Share: <b>${pct.toFixed(1)}%</b></div>`,
                        `<div>Calls: <b>${c.toLocaleString()}</b></div>`,
                    ].join('');
                },
            },
            legend: {
                bottom: 0,
                icon: 'circle',
                itemWidth: 8,
                itemHeight: 8,
                textStyle: { color: '#6b7280' },
            },
            series: [
                {
                    name: 'Share',
                    type: 'pie',
                    radius: ['56%', '78%'],
                    center: ['50%', '45%'],
                    minShowLabelAngle: 8,
                    avoidLabelOverlap: true,
                    label: {
                        show: true,
                        color: '#6b7280',
                        formatter: (p: any) => {
                            const pct = Number(p.percent || 0);
                            if (pct < 6) return '';
                            return `${p.name}\n$${formatMoney(Number(p.value || 0))}`;
                        },
                    },
                    labelLine: {
                        length: 10,
                        length2: 10,
                        lineStyle: { color: 'rgba(156,163,175,0.35)' },
                    },
                    data,
                },
            ],
            graphic: [
                {
                    type: 'text',
                    left: 'center',
                    top: '36%',
                    style: {
                        text: 'Selected total',
                        fill: theme === 'dark' ? '#fff' : '#6b7280',
                        fontSize: 12,
                        fontWeight: 600,
                    },
                },
                {
                    type: 'text',
                    left: 'center',
                    top: '45%',
                    style: {
                        text: `$${formatMoney(total)}`,
                        fill: theme === 'dark' ? '#fff' : '#111827',
                        fontSize: 16,
                        fontWeight: 800,
                    },
                },
            ],
        };
    }, [
        theme,
        chat.v,
        chat.c,
        propertyRuns.v,
        propertyRuns.c,
        scenarioRuns.v,
        scenarioRuns.c,
        getStarted.v,
        getStarted.c,
        scenarioChat.v,
        scenarioChat.c,
    ]);

    if (loading) {
        return (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
                <div className="xl:col-span-8">
                    <div
                        className="w-full rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-4 animate-pulse"
                        style={{ height: 360 }}
                    >
                        <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-700" />
                        <div className="mt-4 h-[70%] w-full rounded bg-gray-200 dark:bg-gray-700" />
                    </div>
                </div>
                <div className="xl:col-span-4">
                    <div
                        className="w-full rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-4 animate-pulse"
                        style={{ height: 360 }}
                    >
                        <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-700" />
                        <div className="mt-4 h-[70%] w-full rounded bg-gray-200 dark:bg-gray-700" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
            <div className="xl:col-span-8 rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-4">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <p className="text-sm font-medium text-black dark:text-white">Composition</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Total uses backend totals, Chats are computed, Runs uses runs_total
                        </p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                        Total: ${formatMoney(totalUsd)} · {totalCalls.toLocaleString()}{' '}
                        {totalCalls === 1 ? 'call' : 'calls'}
                    </p>
                </div>
                <ReactECharts option={stackedOption} style={{ height: 320, width: '100%' }} notMerge lazyUpdate />
            </div>

            <div className="xl:col-span-4 rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-4">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <p className="text-sm font-medium text-black dark:text-white">Share</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Only selected categories</p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                        Selected: $
                        {formatMoney(chat.v + propertyRuns.v + scenarioRuns.v + getStarted.v + scenarioChat.v)}
                    </p>
                </div>
                <ReactECharts option={pieOption} style={{ height: 320, width: '100%' }} notMerge lazyUpdate />
            </div>
        </div>
    );
};

export default CostsCharts;